import { useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import {
  Badge,
  DataTable,
  IconButton,
  Input,
  type BadgeTone,
  type DataTableColumn,
} from '@wundo/ui';
import type { ImportRow } from './importRow.ts';

export interface ImportRowsTableProps {
  rows: ImportRow[];
  /** Off while the import runs: a row being created is not a row being edited. */
  importing: boolean;
  onEdit: (key: string, field: 'firstName' | 'lastName' | 'email', value: string) => void;
  onRemove: (key: string) => void;
}

const STATUS_TONES: Record<ImportRow['status'], BadgeTone> = {
  pending: 'neutral',
  creating: 'info',
  success: 'success',
  error: 'danger',
};

const STATUS_LABELS: Record<ImportRow['status'], string> = {
  pending: 'pending',
  creating: 'creating',
  success: 'success',
  error: 'not added',
};

/**
 * The people about to be created, still editable.
 *
 * A parser this forgiving gets names wrong — a particle read as a first name, a
 * trailing initial — so every cell stays a field until the row goes out. Once a
 * row has succeeded it is frozen: the user exists, and editing the text here
 * would not change them.
 */
export function ImportRowsTable({
  rows,
  importing,
  onEdit,
  onRemove,
}: ImportRowsTableProps) {
  const columns = useMemo<DataTableColumn<ImportRow>[]>(
    () => [
      {
        key: 'status',
        header: 'Status',
        width: 'w-32',
        render: (row) => (
          <div className='flex flex-col items-start gap-1'>
            <Badge tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</Badge>
            {row.errorMessage !== undefined && (
              <span className='text-xs text-danger'>{row.errorMessage}</span>
            )}
          </div>
        ),
      },
      {
        key: 'firstName',
        header: 'First name',
        render: (row) => (
          <Input
            aria-label={`First name for ${row.email}`}
            value={row.firstName}
            disabled={importing || row.status === 'success'}
            onChange={(event) => onEdit(row.key, 'firstName', event.target.value)}
          />
        ),
      },
      {
        key: 'lastName',
        header: 'Last name',
        render: (row) => (
          <Input
            aria-label={`Last name for ${row.email}`}
            value={row.lastName}
            disabled={importing || row.status === 'success'}
            onChange={(event) => onEdit(row.key, 'lastName', event.target.value)}
          />
        ),
      },
      {
        key: 'email',
        header: 'Email',
        render: (row) => (
          <Input
            type='email'
            aria-label={`Email for ${row.email}`}
            value={row.email}
            disabled={importing || row.status === 'success'}
            onChange={(event) => onEdit(row.key, 'email', event.target.value)}
          />
        ),
      },
    ],
    [importing, onEdit],
  );

  return (
    <DataTable
      label='Users to import'
      columns={columns}
      rows={rows}
      rowKey={(row) => row.key}
      actions={(row) => (
        <IconButton
          variant='danger'
          aria-label={`Remove ${row.email}`}
          disabled={importing}
          onClick={() => onRemove(row.key)}
        >
          <Trash2 className='size-4' />
        </IconButton>
      )}
    />
  );
}
